/**
 * attachmentMetadata.js — pure display helpers for the 0.46 required-evidence
 * work (required_evidence_logic.md §2, §5, §6):
 *
 *   - attachmentMetadataLine: the "taken …" / "trainer … · trained …" line
 *     shown under a file pill when an upload carries per-file metadata.
 *   - orderedIncidentKeys / reportIncidentFieldRows: the ordered Treatment
 *     incident-extra keys (matrix-required first, then optional) shared by the
 *     case-file panel, the Edit Incident Details modal and the printable report,
 *     so the three surfaces can never disagree about which fields exist.
 *
 * `formatDate` is injected everywhere so this module stays free of any
 * date-lib / locale choice (the case file uses its own formatDate helper).
 *
 * Metadata shapes (build contract 1):
 *   post_photo           → { takenOn: 'YYYY-MM-DD' }
 *   training_certificate → { trainingDate: 'YYYY-MM-DD', trainerName: string }
 */
import { getRequirements } from './eventVocabularies.js';
import { incidentFieldKeys, incidentFieldLabel, formatIncidentFieldValue } from './incidentFields.js';

const identity = (v) => v;
const isEmpty = (v) => v == null || v === '';

/**
 * Human line for an attachment's upload metadata, or null when there is none.
 * Keyed on the metadata fields (not the category) so a legacy file whose
 * category column is NULL still shows its date when the metadata is present.
 *
 *   { takenOn }                     → "taken 12 Aug 2026"
 *   { trainerName, trainingDate }   → "trainer Jane Doe · trained 5 Mar 2026"
 */
export function attachmentMetadataLine(metadata, { formatDate = identity } = {}) {
  if (!metadata || typeof metadata !== 'object') return null;
  const parts = [];
  if (!isEmpty(metadata.takenOn))      parts.push(`taken ${formatDate(metadata.takenOn)}`);
  if (!isEmpty(metadata.trainerName))  parts.push(`trainer ${String(metadata.trainerName).trim()}`);
  if (!isEmpty(metadata.trainingDate)) parts.push(`trained ${formatDate(metadata.trainingDate)}`);
  return parts.length ? parts.join(' · ') : null;
}

/**
 * Upload-metadata lines for ONE History row: the event's own file
 * (attachment_uploaded → metadata.metadata) plus any comment files folded into
 * it by the timeline grouping (_files[].metadata). A standalone file event
 * already names the file in its label, so its line is bare; folded files are
 * prefixed with their name. [] when the row carries no metadata at all.
 */
export function historyFileMetaLines(ev, { formatDate = identity } = {}) {
  if (!ev || typeof ev !== 'object') return [];
  const out = [];
  const own = attachmentMetadataLine(ev.metadata?.metadata, { formatDate });
  if (own) out.push(own);
  for (const f of Array.isArray(ev._files) ? ev._files : []) {
    const line = attachmentMetadataLine(f?.metadata, { formatDate });
    if (line) out.push(f?.fileName ? `${f.fileName} · ${line}` : line);
  }
  return out;
}

/**
 * Ordered incident-extra keys for a Treatment case.
 *   required — every key incidentFieldKeys() yields that the matrix does NOT
 *              list as optional (matrix-required fields + any stored extra key)
 *   optional — the matrix's optionalFields for (treatment, code), in matrix order
 *   all      — required followed by optional (the display / edit order)
 * Optional keys never appear in `required`, even when incidentFieldKeys()
 * surfaces them because a value is stored.
 */
export function orderedIncidentKeys(treatment, code, typeSpecific) {
  const optional = [...new Set(getRequirements(treatment, code)?.optionalFields || [])];
  const optionalSet = new Set(optional);
  const required = incidentFieldKeys(treatment, code, typeSpecific).filter(k => !optionalSet.has(k));
  return { required, optional, all: [...required, ...optional] };
}

/**
 * Rows for the printable report's Incident Details grid.
 * Required keys are always listed (an empty one renders as a gap, like the
 * case-file panel); optional keys only when they hold a value, unless
 * `includeEmptyOptional` is set.
 * @returns {{ key, label, value: string|null, optional: boolean }[]}
 */
export function reportIncidentFieldRows(treatment, code, typeSpecific, { formatDate = identity, includeEmptyOptional = false } = {}) {
  const ts = typeSpecific && typeof typeSpecific === 'object' ? typeSpecific : {};
  const { required, optional } = orderedIncidentKeys(treatment, code, ts);
  const rows = [];
  for (const key of required) {
    rows.push({ key, label: incidentFieldLabel(key), value: formatIncidentFieldValue(key, ts[key], { formatDate }), optional: false });
  }
  for (const key of optional) {
    // Emptiness is judged on the STORED value, not the display string — a
    // yes/no switch always formats to something ('Not answered'), but an
    // unanswered optional switch is still nothing to print.
    if (isEmpty(ts[key]) && !includeEmptyOptional) continue;
    rows.push({ key, label: incidentFieldLabel(key), value: formatIncidentFieldValue(key, ts[key], { formatDate }), optional: true });
  }
  return rows;
}
